import { type ComputedRef, type Ref, ref, computed } from "vue";

import apiGraph from "@/helpers/api/apiGraph";
import apiSkobo from "@/helpers/api/apiSkobo";

import Permission from "@/models/Documents/Permission";
import FileHelper from "@/helpers/utilities/FileHelper";

import { useMeStore } from "@/stores/me";
import { useSectionsStore } from "@/stores/sections";
import { useSettingsStore } from "@/stores/settings";

interface SharedDriveEntry {
  driveId: string;
  itemId: string;
  displayName?: string;
  canCopyFromRoleRestriction?: string[];
  webUrl?: string;
  [key: string]: unknown;
}

interface DriveGroup {
  parentReference: { driveId: string | null };
  name: string;
  additionalData?: unknown;
  id?: string;
  extension?: ReturnType<typeof FileHelper.getClosedFolderTemplate>;
  isFolder?: boolean;
}

interface SharedDrivesComposableDependencies {
  myDriveRootId: ComputedRef<string | null>;
  myItemPermission: ComputedRef<(driveId: string, itemId: string) => Permission | null>;
}

export const useSharedDrivesComposable = (dependencies: SharedDrivesComposableDependencies) => {
  const sharedDrivesArray: Ref<SharedDriveEntry[]> = ref([]);

  const sharedDrives = computed(() => sharedDrivesArray.value);

  const sharedDrivesICanCopyFilesFrom = computed(() => {
    const meStore = useMeStore();
    const sectionsStore = useSectionsStore();
    const settingsStore = useSettingsStore();

    const driveGroups: DriveGroup[] = [];

    if (settingsStore.copyDocumentsFromOneDrive) {
      driveGroups.push({
        parentReference: { driveId: dependencies.myDriveRootId.value },
        name: "OneDrive"
      });
    }

    if (settingsStore.copyFromShouldIncludeSections) {
      try {
        const sectionDrives = (
          sectionsStore.mySectionsDrives as Array<{ driveId: string; name: string; additionalData?: unknown }>
        ).map(section => ({
          parentReference: { driveId: section.driveId },
          name: section.name,
          additionalData: section.additionalData
        }));
        driveGroups.push(
          ...sectionDrives.sort((first, second) =>
            first.name.localeCompare(second.name, settingsStore.language as string | undefined)
          )
        );
        // eslint-disable-next-line no-empty
      } catch {}
    }

    try {
      const filtered = sharedDrives.value.filter((sharePointFolder: SharedDriveEntry) => {
        if (sharePointFolder.canCopyFromRoleRestriction) {
          if (meStore.isStudent && sharePointFolder.canCopyFromRoleRestriction.indexOf("student") > -1) return true;
          if (meStore.isTeacher && sharePointFolder.canCopyFromRoleRestriction.indexOf("teacher") > -1) return true;
          if (meStore.isSuperuser && sharePointFolder.canCopyFromRoleRestriction.indexOf("superuser") > -1) return true;
        }
        return false;
      });

      driveGroups.push(
        ...filtered
          .filter(
            (sharePointFolder: SharedDriveEntry) =>
              dependencies.myItemPermission.value(sharePointFolder.driveId, sharePointFolder.itemId) &&
              (dependencies.myItemPermission.value(sharePointFolder.driveId, sharePointFolder.itemId)!.myPermission ===
                Permission.nctPermission.WRITE ||
                dependencies.myItemPermission.value(sharePointFolder.driveId, sharePointFolder.itemId)!.myPermission ===
                  Permission.nctPermission.READ ||
                dependencies.myItemPermission.value(sharePointFolder.driveId, sharePointFolder.itemId)!.myPermission ===
                  Permission.nctPermission.OWNER)
          )
          .sort((first: SharedDriveEntry, second: SharedDriveEntry) =>
            (first.displayName ?? "").localeCompare(
              second.displayName ?? "",
              settingsStore.language as string | undefined
            )
          )
          .map((entry: SharedDriveEntry) => ({
            name: entry.displayName ?? "",
            id: entry.itemId,
            parentReference: { driveId: entry.driveId },
            extension: FileHelper.getClosedFolderTemplate(),
            isFolder: true
          }))
      );
      // eslint-disable-next-line no-empty
    } catch {}
    return driveGroups;
  });

  const reloadSharedDrives = async () => {
    const result = await apiSkobo.get("/appsharepointfolder");
    sharedDrivesArray.value = (result.data as SharedDriveEntry[]).map(entry => {
      entry.driveId = entry.driveId.trim();
      return entry;
    });
  };

  const loadSharedDrives = async () => {
    if (sharedDrivesArray.value.length === 0) {
      await reloadSharedDrives();
    }
  };

  const loadSharedDrivesWebUrl = async (driveId: string, itemId: string) => {
    const result = await apiGraph.get(`/drives/${driveId}/items/${itemId}?$SELECT=webUrl`);
    // Note: this code mutates sharedDrivesArray as if it were a 2-key map; preserved as-is for legacy behaviour.
    const sharedDrivesClone = sharedDrivesArray.value as unknown as Record<string, Record<string, { webUrl?: string }>>;
    if (!sharedDrivesClone[driveId]) {
      sharedDrivesClone[driveId] = {};
    }
    if (!sharedDrivesClone[driveId][itemId]) {
      sharedDrivesClone[driveId][itemId] = {};
    }
    sharedDrivesClone[driveId][itemId].webUrl = result.data.webUrl;
    sharedDrivesArray.value = sharedDrivesClone as unknown as SharedDriveEntry[];
    return result.data.webUrl;
  };

  return {
    sharedDrivesArray,
    sharedDrives,
    sharedDrivesICanCopyFilesFrom,
    loadSharedDrives,
    reloadSharedDrives,
    loadSharedDrivesWebUrl
  };
};
